/**
 * agentActionBridge — anti-corruption boundary for agent ui_action SSE frames.
 *
 * Sole action→behavior translator (DIP): allow-list-validates the action name,
 * runs a per-action payload guard, then emits the matching eventBus modal-open
 * event. Unknown action OR invalid payload → DROP + structured log (NO throw).
 *
 * Security invariant: an action is bridge-eligible only if its target modal
 * re-derives sensitive data from the id via a JWT-guarded fetch (both
 * ClienteModal and PDVCheckoutModal do this).
 *
 * NOTE: id-dedup is a no-op until stream-resume exists (idempotency DEFERRED).
 */

import { eventBus } from '../eventBus'
import { log } from '../utils/DebugLogger'
import type { AgentEventUiAction } from '../composables/aiAgentContract'

/**
 * SSOT (FE side) mirror of the agent catalog's CLIENT_ACTION_NAMES tuple
 * (services/ai-agent-service/src/actions/catalog.ts). The FE is a separate
 * package with no path to the agent repo, so the tuple is redeclared here and
 * a conformance test keys the handler-map off it. Keep in lockstep with catalog.ts.
 */
export const CLIENT_ACTION_NAMES = ['open_cliente_modal', 'open_so_order_modal'] as const

type ClientActionName = (typeof CLIENT_ACTION_NAMES)[number]

/** Per-action payload guard + handler. Returns false if the payload is invalid. */
type ActionHandler = (payload: unknown) => boolean

const HANDLER_MAP: Record<ClientActionName, ActionHandler> = {
  open_cliente_modal: (payload) => {
    if (
      typeof payload !== 'object' ||
      payload === null ||
      typeof (payload as Record<string, unknown>)['clienteId'] !== 'number'
    ) {
      return false
    }
    eventBus.emit('modal.cliente.open', { id: (payload as { clienteId: number }).clienteId })
    return true
  },
  open_so_order_modal: (payload) => {
    if (
      typeof payload !== 'object' ||
      payload === null ||
      typeof (payload as Record<string, unknown>)['orderId'] !== 'number'
    ) {
      return false
    }
    eventBus.emit('modal.pdv-checkout.open', { orderId: (payload as { orderId: number }).orderId })
    return true
  },
}

function dispatch(ev: AgentEventUiAction): void {
  if (!(CLIENT_ACTION_NAMES as readonly string[]).includes(ev.action)) {
    log('agentActionBridge', 'dispatch', 'ui_action descartada: acao desconhecida', {
      action: ev.action,
      id: ev.id,
    })
    return
  }

  const handler = HANDLER_MAP[ev.action as ClientActionName]
  const ok = handler(ev.payload)
  if (!ok) {
    log('agentActionBridge', 'dispatch', 'ui_action descartada: payload invalido', {
      action: ev.action,
      id: ev.id,
    })
  }
}

export const agentActionBridge = { dispatch }
